/**
 * DAC profiles -- group 01 (high-end converters, ladder and delta-sigma).
 *
 * The eight entries below are chosen so that no two of them compile to the same
 * DSP chain. They differ in conversion architecture (FPGA-mapped ring ladder,
 * discrete module ladder, monolithic 20-bit ladder, switched-resistor multibit,
 * classic multi-bit delta-sigma), in output topology (passive I/V with no
 * active stage at all, discrete Class A, JFET buffer, op-amp), in whether they
 * oversample or hold, and in the size and *structure* of the residual
 * distortion. As elsewhere in this database, published numbers are tagged as
 * such and everything the kit expands from them is DERIVED or MODELLED. None of
 * these entries is a measurement.
 *
 * Note on honesty: none of the eight manufacturers in this group ships a
 * tube-output or transformer-output DAC, so no entry here claims one.
 */

import type { DacProfile } from '../../types/hardware';
import {
  AKM_FILTERS,
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  makeDac,
  usd,
} from '../kit';

export const DACS_01: DacProfile[] = [
  makeDac({
    mfr: 'dCS',
    model: 'Rossini APEX DAC',
    price: usd(
      29000,
      'APPROXIMATE',
      2023,
      'US list for the APEX version varies by dealer and by whether the unit is a factory build or an APEX upgrade of an earlier Rossini; treat as an approximate figure.',
    ),
    year: 2022,
    country: 'United Kingdom',
    architecture: 'fpga-custom',
    core: 'dCS Ring DAC: 5-bit oversampled current-source array with pseudo-random element mapping, FPGA sequenced',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 118,
    thdPlusNDb: -113,
    crosstalkDb: -115,
    outputVrmsSE: 2.0,
    outputVrmsBal: 6.0,
    outputImpedanceOhms: 3,
    balanced: true,
    dsd: 'dsd128',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: [
      { ...FILTERS.sharpLinear(128), id: 'dcs-f1', label: 'Filter 1 (sharp, linear phase)' },
      { ...FILTERS.slowLinear(48), id: 'dcs-f2', label: 'Filter 2 (slow, linear phase)' },
      { ...FILTERS.sharpMinimum(96), id: 'dcs-f3', label: 'Filter 3 (asymmetric, minimum phase)' },
      { ...FILTERS.apodizing(128), id: 'dcs-f4', label: 'Filter 4 (apodizing)' },
      { ...FILTERS.hybrid(96), id: 'dcs-f5', label: 'Filter 5 (hybrid phase)' },
    ],
    defaultFilterId: 'dcs-f1',
    effectiveBits: 20,
    jitterPsRms: 10,
    internalOversampling: 64,
    slewRateVus: 30,
    confidence: 'ESTIMATED',
    accent: '#9fb6cd',
    tags: ['ring-dac', 'flagship', 'upsampling', 'network'],
    notes:
      'The Ring DAC is a small (5-bit) array of nominally identical current sources fed by an oversampling modulator, with a mapper that changes which physical sources are used from sample to sample. That trade is the whole point of the design: element mismatch that a fixed ladder would express as static integral nonlinearity, and therefore as level-correlated harmonics, is instead spread into a broadband noise term. The profile therefore models a slightly higher noise floor than the best delta-sigma chipsets but a distortion residue that does not track signal level, which is the opposite behaviour to the discrete ladders below.',
    provenanceNote:
      'Architecture, oversampling behaviour, filter count and output configuration follow dCS published material. The dynamic range and THD+N values are approximate recollections of published figures and should be re-checked against the current datasheet before any measurement claim is made; element-mapping noise behaviour is modelled, not measured.',
  }),

  makeDac({
    mfr: 'MSB Technology',
    model: 'Discrete DAC',
    price: usd(
      9950,
      'APPROXIMATE',
      2022,
      'Base configuration. MSB prices the chassis, the converter modules, the clock and the input boards separately, so a delivered unit is usually well above this figure.',
    ),
    year: 2019,
    country: 'United States',
    architecture: 'hybrid',
    core: 'MSB Discrete ladder DAC modules, dual mono, no off-the-shelf converter IC',
    outputStage: 'passive-iv',
    dynamicRangeDb: 126,
    thdPlusNDb: -110,
    crosstalkDb: -125,
    outputVrmsSE: 2.1,
    outputVrmsBal: 4.2,
    outputImpedanceOhms: 150,
    balanced: true,
    dsd: 'dsd256',
    filters: [FILTERS.sharpLinear(256), FILTERS.nosCompensated(), FILTERS.slowMinimum(32)],
    defaultFilterId: 'sharp-linear',
    ladderMismatchLsb: 0.22,
    effectiveBits: 21,
    jitterPsRms: 12,
    confidence: 'ESTIMATED',
    accent: '#d8d2c4',
    tags: ['ladder', 'passive-iv', 'modular', 'dual-mono'],
    notes:
      'There is no active gain stage in the signal path: the ladder modules deliver current and a resistor converts it to voltage, so the output impedance is the I/V resistor itself rather than the vanishing figure of a feedback buffer. Two consequences are modelled here. First, no op-amp loop means no loop-gain-dependent high-order residue, so what distortion remains is the ladder INL, and the trimmed modules keep that low. Second, a 150 ohm source into a cable and a preamp input forms a real divider and a real low-pass, so this DAC is the one entry in the group whose measured response depends on what follows it.',
    provenanceNote:
      'Architecture, dual-mono layout and the passive current-to-voltage output are from MSB published descriptions. MSB does not publish conventional THD+N or A-weighted dynamic range figures, so both are ESTIMATED from the class of design; ladder mismatch in LSB is an estimate consistent with laser-trimmed precision networks.',
  }),

  makeDac({
    mfr: 'Esoteric',
    model: 'D-02X',
    price: usd(24000, 'APPROXIMATE', 2019, 'US list at introduction; the model has since been superseded.'),
    year: 2018,
    country: 'Japan',
    architecture: 'multibit-delta-sigma',
    core: 'AKM AK4497 switched-resistor multibit converters, four paralleled per channel, 34-bit internal processing',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 120,
    thdPlusNDb: -110,
    crosstalkDb: -118,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 12,
    balanced: true,
    dsd: 'dsd512',
    filters: AKM_FILTERS(),
    defaultFilterId: 'sharp-linear',
    parallelUnits: 4,
    noiseShaperOrder: 5,
    idleToneLevel: 0.03,
    jitterPsRms: 15,
    slewRateVus: 45,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a8f9e',
    tags: ['akm', 'parallel-dac', 'hcld', 'dual-mono'],
    notes:
      'Four converters per channel are run in parallel and summed. Their signal content is correlated and their modulator noise is not, so the summation buys roughly 6 dB of noise floor per quadrupling while leaving distortion untouched -- which is why this entry pairs a high dynamic range with a distortion figure no better than a single good chip. The discrete high-current buffer that follows is specified for slew rather than for minimum distortion, so the model gives it a high slew limit and a low output impedance rather than an unusually clean transfer curve.',
    provenanceNote:
      'Converter type, parallel configuration, output voltages and the discrete buffer topology follow Esoteric published specifications; the filter set follows the AKM roll-off options the chipset exposes. Jitter, parallel-summing noise benefit and the modelled harmonic distribution are DERIVED, not measured.',
  }),

  makeDac({
    mfr: 'Holo Audio',
    model: 'May KTE',
    price: usd(
      5598,
      'APPROXIMATE',
      2023,
      'Kitsune Tuned Edition US retail; Holo Audio sells the May in several trim levels and the figure moves with them.',
    ),
    year: 2020,
    country: 'China',
    architecture: 'discrete-r2r',
    core: 'Discrete resistor ladders, separate PCM and DSD ladder pairs per channel, FPGA linear-compensation control',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 121,
    thdPlusNDb: -112,
    crosstalkDb: -118,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 15,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.nos(), FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.3,
    effectiveBits: 21,
    jitterPsRms: 30,
    internalOversampling: 1,
    harmonicShape: HARMONIC_SHAPES.r2r,
    confidence: 'ESTIMATED',
    accent: '#b48ead',
    tags: ['r2r', 'nos', 'discrete', 'dual-ladder'],
    notes:
      'Two independent ladder pairs per channel, one carrying PCM and one carrying the DSD bitstream, so a DSD file never passes through a PCM decimation stage. In the default non-oversampling mode there is no interpolation filter at all: the zero-order hold imposes the sin(x)/x envelope this profile applies as a rate-dependent response, and the images above Nyquist are left in place for the analog stage to deal with. Distortion is level-dependent ladder INL rather than the level-independent floor of a delta-sigma part, so its relative magnitude grows as the signal gets quieter.',
    provenanceNote:
      'Dual-ladder architecture, NOS/OS switching and output levels are from Holo Audio published material. THD+N and dynamic range are ESTIMATED at the level widely reported for the design rather than quoted from a datasheet; ladder mismatch in LSB is inferred from the design class. The NOS droop curve is computed from the zero-order-hold transfer function and is therefore DERIVED.',
  }),

  makeDac({
    mfr: 'Schiit Audio',
    model: 'Yggdrasil',
    price: usd(
      2399,
      'APPROXIMATE',
      2023,
      'Yggdrasil has listed between roughly USD 2,299 and 2,599 across its A1, A2, Less Is More and More Is Better revisions; figure is a mid-range approximation.',
    ),
    year: 2015,
    country: 'United States',
    architecture: 'ladder-multibit',
    core: 'Four Analog Devices AD5791 20-bit monolithic ladder converters, two per channel, balanced',
    outputStage: 'jfet',
    dynamicRangeDb: 115,
    thdPlusNDb: -98,
    crosstalkDb: -100,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 75,
    balanced: true,
    dsd: 'none',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [
      { ...FILTERS.sharpLinear(256), id: 'closed-form', label: 'Closed-form original-sample filter' },
      FILTERS.nos(),
    ],
    defaultFilterId: 'closed-form',
    ladderMismatchLsb: 0.3,
    effectiveBits: 20,
    jitterPsRms: 55,
    confidence: 'ESTIMATED',
    accent: '#8c9a5b',
    tags: ['ladder', 'ad5791', 'no-dsd', 'closed-form-filter'],
    notes:
      'A true 20-bit monolithic ladder per half-channel, which caps the achievable noise floor well above what a modern delta-sigma part reaches and makes the 20-bit quantisation itself an audible modelling term rather than a rounding detail. The interpolation filter is a closed-form design that leaves the original input samples untouched at their original instants and computes only the intermediate points, so pre-ringing is present but the arrival time of a transient is not shifted. There is no DSD path anywhere in the unit, which is a genuine capability difference rather than a voicing one.',
    provenanceNote:
      'Converter part, channel configuration, the absence of DSD support and the 192 kHz rate ceiling follow Schiit published material. THD+N and dynamic range are ESTIMATED from the resolution of the converter and typical published figures rather than quoted; the closed-form filter is modelled as a long linear-phase prototype because its actual coefficients are not published.',
  }),

  makeDac({
    mfr: 'Gustard',
    model: 'R26',
    price: usd(
      1699,
      'APPROXIMATE',
      2023,
      'Typical US/EU street price through Gustard distributors; the brand does not publish a fixed regional list price.',
    ),
    year: 2022,
    country: 'China',
    architecture: 'r2r',
    core: 'Discrete 24-bit R-2R resistor networks, four per channel, FPGA-controlled, integrated network renderer',
    outputStage: 'op-amp',
    dynamicRangeDb: 120,
    thdPlusNDb: -106,
    crosstalkDb: -112,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.nosCompensated(), FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    defaultFilterId: 'nos-comp',
    ladderMismatchLsb: 0.45,
    effectiveBits: 20,
    jitterPsRms: 35,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c9975c',
    tags: ['r2r', 'nos', 'streamer', 'value'],
    notes:
      'A discrete ladder built from ordinary thin-film resistors rather than trimmed networks, which is what separates it from the MSB and Holo entries: the same architecture with roughly twice the element mismatch, so the low-level integral nonlinearity term the model applies is correspondingly larger and the distortion residue stays proportionally constant as level falls. The default oversampled mode adds a droop-compensating hold rather than a full-length interpolation filter, so the sin(x)/x correction is applied digitally instead of being left to the analog stage.',
    provenanceNote:
      'Architecture, resistor-network count, output levels and the OS/NOS switching follow Gustard published specifications; the quoted dynamic range and THD+N are the manufacturer figures as recalled and should be re-verified before being cited. The ladder mismatch value is ESTIMATED from the class of resistor used, not published.',
  }),

  makeDac({
    mfr: 'SMSL',
    model: 'VMV D2R',
    price: usd(
      1599,
      'APPROXIMATE',
      2023,
      'SMSL flagship; street price has moved roughly between USD 1,500 and 1,800 since launch.',
    ),
    year: 2022,
    country: 'China',
    architecture: 'delta-sigma',
    core: 'ROHM BD34301EKV 32-bit converter with dual-mono clocking and low-oversampling modulator',
    outputStage: 'op-amp',
    dynamicRangeDb: 130,
    thdPlusNDb: -122,
    crosstalkDb: -140,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd512',
    filters: [
      FILTERS.sharpLinear(128),
      FILTERS.slowLinear(48),
      FILTERS.sharpMinimum(96),
      FILTERS.slowMinimum(32),
    ],
    defaultFilterId: 'sharp-linear',
    noiseShaperOrder: 5,
    idleToneLevel: 0.05,
    jitterPsRms: 18,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5f9ea0',
    tags: ['rohm', 'delta-sigma', 'measurement-led'],
    notes:
      'The ROHM part is the distinguishing element: it runs a lower oversampling ratio and a shorter digital filter than the ESS and AKM alternatives, which trades ultimate stopband depth for less shaped noise pushed just above the audio band. The profile therefore models a high in-band dynamic range together with a noise shaping knee that is less aggressive than the ESS-based Musetec entry, and a distortion residue that is dominated by the op-amp output stage rather than by the modulator.',
    provenanceNote:
      'Converter part and the headline dynamic range and THD+N figures are SMSL published specifications as recalled; SMSL publishes these prominently but the values here are approximate to within a decibel or two. Noise shaper order, idle-tone tendency and jitter are ESTIMATED from the chipset class.',
  }),

  makeDac({
    mfr: 'Musetec',
    model: 'MH-DA005',
    price: usd(
      2299,
      'APPROXIMATE',
      2022,
      'Direct-sale price; has ranged roughly USD 2,100 to 2,500 depending on the seller.',
    ),
    year: 2021,
    country: 'China',
    architecture: 'delta-sigma',
    core: 'Two ESS ES9038PRO converters, eight internal channels paralleled per side, discrete I/V',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 128,
    thdPlusNDb: -118,
    crosstalkDb: -135,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 47,
    balanced: true,
    dsd: 'dsd512',
    filters: ESS_FILTERS(),
    defaultFilterId: 'sharp-linear',
    parallelUnits: 8,
    noiseShaperOrder: 6,
    idleToneLevel: 0.04,
    jitterPsRms: 25,
    slewRateVus: 25,
    confidence: 'ESTIMATED',
    accent: '#a05c7b',
    tags: ['ess', 'parallel-dac', 'discrete-iv', 'linear-supply'],
    notes:
      'One ES9038PRO per channel with all eight of its internal converters summed, feeding a discrete current-to-voltage stage rather than the usual op-amp I/V. Paralleling averages the modulator noise down without changing the modulator itself, so the signature that survives is the ESS one: a very low distortion floor with steeply shaped noise above the audio band, and the well-documented sensitivity of the chipset filter selection to the incoming sample rate. The discrete I/V raises output impedance well above an op-amp stage and is modelled with a Class A style low-order harmonic residue on top of the modulator floor.',
    provenanceNote:
      'Converter part, paralleling arrangement and the discrete current-to-voltage stage follow Musetec published descriptions. Dynamic range and THD+N are ESTIMATED at levels typical of well-implemented dual ES9038PRO designs; Musetec does not publish a full measurement suite. Filter set follows the ESS chipset roll-off options.',
  }),
];
